import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { navy:'#0D1B2A', teal:'#00B4D8', success:'#10B981', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA', amber:'#F59E0B' };

function StatPill({ label, value, color, unit }) {
  return (
    <div style={{ background:C.bg, borderRadius:10, padding:'10px 14px', textAlign:'center', flex:1 }}>
      <div style={{ fontSize:20, fontWeight:800, color: color || C.navy }}>{value}<span style={{ fontSize:12, fontWeight:500, color:C.grey, marginLeft:2 }}>{unit}</span></div>
      <div style={{ fontSize:11, color:C.grey, marginTop:2 }}>{label}</div>
    </div>
  );
}

export default function AdminAttendance() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const [tab, setTab] = useState('tutors'); // 'tutors' | 'students'
  const [tutorData,   setTutorData]   = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [checkingOutStudent, setCheckingOutStudent] = useState(null);

  const handleStudentCheckout = async (attId, studentName) => {
    if (!window.confirm(`Manually check out ${studentName}?`)) return;
    setCheckingOutStudent(attId);
    try {
      const r = await api.post(`/api/admin/attendance/${attId}/checkout`);
      toast.success(r.data.message || 'Checked out');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to check out');
    } finally {
      setCheckingOutStudent(null);
    }
  };

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get(`/api/admin/tutor-attendance?month=${month}`).catch(() => null),
      api.get(`/api/admin/student-attendance?month=${month}`).catch(() => null),
    ]).then(([tr, sr]) => {
      setTutorData(tr?.data || null);
      setStudentData(sr?.data || null);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); setExpanded(null); }, [month]); // eslint-disable-line

  const handleCheckout = async (e, tutorIdNumber) => {
    e.stopPropagation();
    setCheckingOut(tutorIdNumber);
    try {
      const r = await api.post('/api/admin/tutor-checkin', { tutor_id_number: tutorIdNumber });
      if (r.data.action === 'checkout') {
        const dur = r.data.duration_min;
        toast.success(`${r.data.tutor_name} checked out — ${Math.floor(dur/60)}h ${dur%60}m on site`);
        fetchAll();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Checkout failed');
    }
    setCheckingOut(null);
  };

  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return { value: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`,
             label: d.toLocaleDateString('en-GB', { month:'long', year:'numeric' }) };
  });

  const tutors   = tutorData?.tutors   || [];
  const students = studentData?.students || [];

  const tutorTotals = tutors.reduce((acc, t) => ({
    physical_days:    acc.physical_days    + t.physical_days,
    online_sessions:  acc.online_sessions  + t.online_sessions,
    total_hours:      acc.total_hours      + t.total_hours,
  }), { physical_days:0, online_sessions:0, total_hours:0 });

  const studentTotals = students.reduce((acc, s) => ({
    days:        acc.days        + s.days,
    total_hours: acc.total_hours + s.total_hours,
  }), { days:0, total_hours:0 });

  return (
    <Layout role="admin">
      <div style={{ maxWidth:800, margin:'0 auto' }}>

        {/* Header row */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
          <div>
            <h2 style={{ margin:'0 0 4px', fontSize:22, fontWeight:800, color:C.navy }}>Attendance</h2>
            <p style={{ margin:0, fontSize:14, color:C.grey }}>Physical check-ins and online sessions</p>
          </div>
          <select value={month} onChange={e => setMonth(e.target.value)}
            style={{ padding:'8px 14px', border:'1.5px solid #E5E7EB', borderRadius:10, fontSize:14, background:'#fff', color:C.navy, fontWeight:600 }}>
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:4, marginBottom:20, background:'#F1F5F9', borderRadius:12, padding:4 }}>
          {[['tutors','🎓 Tutors'], ['students','🎒 Students']].map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); setExpanded(null); }}
              style={{ flex:1, padding:'10px 0', border:'none', borderRadius:9, fontWeight:700, fontSize:14, cursor:'pointer',
                       background: tab === key ? '#fff' : 'transparent',
                       color: tab === key ? C.navy : C.grey,
                       boxShadow: tab === key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none' }}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:C.grey }}>Loading attendance data…</div>
        ) : tab === 'tutors' ? (

          /* ── TUTORS TAB ── */
          <>
            {tutors.length > 0 && (
              <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                <StatPill label="Total Physical Days"  value={tutorTotals.physical_days}             color={C.success} unit="days"     />
                <StatPill label="Online Sessions"      value={tutorTotals.online_sessions}            color={C.teal}    unit="sessions" />
                <StatPill label="Total Hours"          value={tutorTotals.total_hours.toFixed(1)}     color={C.navy}    unit="hrs"      />
              </div>
            )}
            {tutors.length === 0 ? (
              <div style={{ background:'#fff', borderRadius:14, padding:48, textAlign:'center' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
                <div style={{ fontWeight:700, fontSize:16, color:C.navy, marginBottom:6 }}>No tutor attendance data</div>
                <div style={{ fontSize:14, color:C.grey }}>No approved tutors or no check-ins recorded for this month.</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {tutors.map(t => (
                  <div key={t.tutor_id} style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', overflow:'hidden' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 20px', cursor:'pointer' }}
                      onClick={() => setExpanded(expanded === t.tutor_id ? null : t.tutor_id)}>
                      <div style={{ width:42, height:42, borderRadius:'50%', background:C.bg, overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:16, color:C.teal }}>
                        {t.photo_url
                          ? <img src={t.photo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                          : (t.name||'T')[0].toUpperCase()}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:14, color:C.navy }}>{t.name}</div>
                        <div style={{ fontSize:12, color:C.grey, fontFamily:'monospace' }}>{t.tutor_id_number}</div>
                      </div>
                      {t.recent_checkins.some(c => !c.time_out) && (
                        <button
                          onClick={e => handleCheckout(e, t.tutor_id_number)}
                          disabled={checkingOut === t.tutor_id_number}
                          style={{ padding:'5px 12px', background:'#FEF9C3', color:'#92400E', border:'1.5px solid #FDE68A', borderRadius:20, fontWeight:700, fontSize:12, cursor:'pointer', whiteSpace:'nowrap' }}>
                          {checkingOut === t.tutor_id_number ? '…' : '🟡 Still In — Check Out'}
                        </button>
                      )}
                      <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                        <div style={{ textAlign:'center' }}>
                          <div style={{ fontWeight:800, fontSize:18, color:C.success }}>{t.physical_days}</div>
                          <div style={{ fontSize:10, color:C.grey }}>days in</div>
                        </div>
                        <div style={{ textAlign:'center' }}>
                          <div style={{ fontWeight:800, fontSize:18, color:C.teal }}>{t.online_sessions}</div>
                          <div style={{ fontSize:10, color:C.grey }}>online</div>
                        </div>
                        <div style={{ textAlign:'center' }}>
                          <div style={{ fontWeight:800, fontSize:18, color:C.navy }}>{t.total_hours}h</div>
                          <div style={{ fontSize:10, color:C.grey }}>total</div>
                        </div>
                      </div>
                      <div style={{ fontSize:16, color:C.grey, marginLeft:4 }}>{expanded === t.tutor_id ? '▲' : '▼'}</div>
                    </div>
                    {expanded === t.tutor_id && (
                      <div style={{ borderTop:'1px solid #F1F5F9', padding:'12px 20px', background:'#F9FAFB' }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.grey, marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 }}>Recent Physical Check-ins</div>
                        {t.recent_checkins.length === 0 ? (
                          <div style={{ fontSize:13, color:C.grey, fontStyle:'italic' }}>No physical check-ins this month</div>
                        ) : (
                          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                            {t.recent_checkins.map((c, i) => (
                              <div key={i} style={{ display:'flex', gap:12, fontSize:13, alignItems:'center' }}>
                                <span style={{ fontWeight:600, color:C.navy, width:60 }}>{c.date}</span>
                                <span style={{ color:C.success }}>In: {c.time_in}</span>
                                {c.time_out && <span style={{ color:'#1D4ED8' }}>Out: {c.time_out}</span>}
                                {c.duration_min && <span style={{ color:C.grey, fontSize:12 }}>({Math.floor(c.duration_min/60)}h {c.duration_min%60}m)</span>}
                                {!c.time_out && <span style={{ background:'#FEF9C3', color:'#92400E', fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:600 }}>Still in</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>

        ) : (

          /* ── STUDENTS TAB ── */
          <>
            {students.length > 0 && (
              <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                <StatPill label="Total Days In"  value={studentTotals.days}                  color={C.success} unit="days" />
                <StatPill label="Total Hours"    value={studentTotals.total_hours.toFixed(1)} color={C.navy}    unit="hrs"  />
                <StatPill label="Students"       value={students.length}                      color={C.teal}    unit=""     />
              </div>
            )}
            {students.length === 0 ? (
              <div style={{ background:'#fff', borderRadius:14, padding:48, textAlign:'center' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🎒</div>
                <div style={{ fontWeight:700, fontSize:16, color:C.navy, marginBottom:6 }}>No student attendance data</div>
                <div style={{ fontSize:14, color:C.grey }}>No student check-ins recorded for this month.</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {students.map(s => (
                  <div key={s.student_id} style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', overflow:'hidden' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 20px', cursor:'pointer' }}
                      onClick={() => setExpanded(expanded === s.student_id ? null : s.student_id)}>
                      <div style={{ width:42, height:42, borderRadius:'50%', background:'#F0FDF4', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:16, color:C.success }}>
                        {(s.name||'S')[0].toUpperCase()}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:14, color:C.navy, textTransform:'capitalize' }}>{s.name}</div>
                        <div style={{ fontSize:12, color:C.grey, fontFamily:'monospace' }}>{s.school_id_number}</div>
                      </div>
                      {s.recent_checkins.some(c => c.still_in) && (
                        <span style={{ padding:'4px 10px', background:'#FEF9C3', color:'#92400E', border:'1.5px solid #FDE68A', borderRadius:20, fontWeight:700, fontSize:12, whiteSpace:'nowrap' }}>
                          🟡 Still In
                        </span>
                      )}
                      <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                        <div style={{ textAlign:'center' }}>
                          <div style={{ fontWeight:800, fontSize:18, color:C.success }}>{s.days}</div>
                          <div style={{ fontSize:10, color:C.grey }}>days in</div>
                        </div>
                        <div style={{ textAlign:'center' }}>
                          <div style={{ fontWeight:800, fontSize:18, color:C.navy }}>{s.total_hours}h</div>
                          <div style={{ fontSize:10, color:C.grey }}>total</div>
                        </div>
                      </div>
                      <div style={{ fontSize:16, color:C.grey, marginLeft:4 }}>{expanded === s.student_id ? '▲' : '▼'}</div>
                    </div>
                    {expanded === s.student_id && (
                      <div style={{ borderTop:'1px solid #F1F5F9', padding:'12px 20px', background:'#F9FAFB' }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.grey, marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 }}>Check-in History</div>
                        {s.recent_checkins.length === 0 ? (
                          <div style={{ fontSize:13, color:C.grey, fontStyle:'italic' }}>No check-ins this month</div>
                        ) : (
                          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                            {s.recent_checkins.map((c, i) => (
                              <div key={i} style={{ display:'flex', gap:12, fontSize:13, alignItems:'center' }}>
                                <span style={{ fontWeight:600, color:C.navy, width:60 }}>{c.date}</span>
                                <span style={{ color:C.success }}>In: {c.time_in}</span>
                                {c.time_out && <span style={{ color:'#1D4ED8' }}>Out: {c.time_out}</span>}
                                {c.duration_min ? <span style={{ color:C.grey, fontSize:12 }}>({Math.floor(c.duration_min/60)}h {c.duration_min%60}m)</span> : null}
                                {c.still_in && (
                                  <>
                                    <span style={{ background:'#FEF9C3', color:'#92400E', fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:600 }}>Still in</span>
                                    <button
                                      onClick={e => { e.stopPropagation(); handleStudentCheckout(c.att_id, s.name); }}
                                      disabled={checkingOutStudent === c.att_id}
                                      style={{ fontSize:11, padding:'2px 10px', background:'#EF4444', color:'#fff', border:'none', borderRadius:20, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                                      {checkingOutStudent === c.att_id ? '…' : '✓ Check Out'}
                                    </button>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      </div>
    </Layout>
  );
}
